import { Box, Button } from '@mui/material'
import colors from '../../constants/colors'
import timeUtils from '../../utils/timeUtils'

import { ReactComponent as DownIcon } from '../../assets/images/DownIcon.svg'
import { ReactComponent as PinIcon } from '../../assets/images/PinIcon.svg'
import { ReactComponent as TrashIcon } from '../../assets/images/TrashIcon.svg'
import { IFileDTO } from '../../store/materials/types'
import { IFilterObject } from '../FilterComponent'
import { uploadInputZones } from '../UploadDropzone/types'
import { resolveFileType } from './DataPart'
import DescriptionIcon from '@mui/icons-material/Description'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import { IFile } from './types'

const sectionNames: { [key: string]: string } = {
  user: 'People',
  media_type: 'Media',
}

const defaultFilter: IFilterObject[] = [
  {
    title: 'Media',
    value: 'media_type',
    section: true,
    checked: false,
    subTypes: [
      {
        title: 'Audio',
        value: 'audio',
        checked: false,
        section: false,
      },
      {
        title: 'Video',
        value: 'video',
        checked: false,
        section: false,
      },
      {
        title: 'Image',
        value: 'image',
        checked: false,
        section: false,
      },
      {
        title: 'PDF',
        value: 'document',
        checked: false,
        section: false,
      },
    ],
  },
]
const boxStyle = { width: '10rem', height: '10rem' }
const iconStyle = { width: '100%', height: '100%', color: colors.TAG_PURPLE }

const testColumns = (setIsPinned: any, setIsTrash: any, downloadFile: any, previewOnly = false) => [
  {
    id: 'preview',
    label: 'Preview',
    format: (_: any, row: IFileDTO) => {
      let url = ''
      if (row instanceof File) {
        url = URL.createObjectURL(row)
      }

      const getBoxStyle = (iconStyle: any) => <Box sx={[boxStyle]}>{iconStyle}</Box>

      const getMediaElement = (src: string) => (
        <img src={src} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
      )

      if (row?.extension === uploadInputZones.TEXT_FIELD) {
        return getBoxStyle(<TextFieldsIcon sx={[iconStyle]} />)
      }

      if (row?.extension?.includes('text/') || row?.extension?.includes('aplication/')) {
        return (
          <Box sx={{ width: '10rem', height: '10rem' }}>
            <DescriptionIcon sx={[iconStyle]} />
          </Box>
        )
      }

      const typeToCheck = row?.extension ?? row?.extension
      if (resolveFileType(typeToCheck) === uploadInputZones.AUDIO) {
        return getBoxStyle(<AudioFileIcon sx={[iconStyle]} />)
      }

      if (resolveFileType(typeToCheck) === uploadInputZones.VIDEO) {
        return (
          <Box sx={[boxStyle]} id='video'>
            <video style={{ objectFit: 'cover', width: '100%', height: '100%' }}>
              <source src={row instanceof File ? url : row?.link}></source>
            </video>
          </Box>
        )
      }

      if (row?.link) {
        return getBoxStyle(getMediaElement(row instanceof File ? url : row?.link))
      }

      return getBoxStyle(getMediaElement(row instanceof File ? url : row?.link))
    },
  },
  {
    id: 'name',
    label: 'File Name',
    format: (_: any, row: any) => {
      return (
        <Box
          sx={{
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row?.name}
        </Box>
      )
    },
  },
  // {
  //   id: 'uploaded_by',
  //   label: 'Uploaded By',
  //   format: (_: any, row: any) => {
  //     return (
  //       <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
  //         {row?.uploader?.first_name} {row?.uploader?.last_name}
  //       </Box>
  //     )
  //   },
  // },
  {
    id: 'uploaded_at',
    label: 'Upload Date',
    format: (row: IFileDTO) => {
      return timeUtils.getFormatedDate(row?.uploaded_at)
    },
  },
  {
    id: 'actions',
    format: (_: any, row: IFileDTO) => {
      return (
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          <Button
            startIcon={<DownIcon fill={colors.TAG_LINES_GRAY} />}
            sx={{ minWidth: 'unset', padding: 0, margin: 0 }}
            onClick={e => {
              e.stopPropagation()
              downloadFile({ fileUrl: row?.link, fileName: row?.name })
            }}
            disabled={previewOnly}
          />
          <Button
            startIcon={<PinIcon fill={row?.is_pinned ? colors.TAG_RED : colors.TAG_LINES_GRAY} />}
            onClick={e => {
              e.stopPropagation()
              setIsPinned(row?.id, row)
            }}
            sx={{ minWidth: 'unset', padding: 0, margin: 0 }}
            disabled={previewOnly}
          />
          <Button
            startIcon={
              <TrashIcon
                fill={row?.is_deleted ? colors.BLACK : colors.TAG_LINES_GRAY}
                stroke={row?.is_deleted ? colors.BLACK : colors.TAG_LINES_GRAY}
              />
            }
            onClick={e => {
              e.stopPropagation()
              setIsTrash(row?.id, row)
            }}
            sx={{ minWidth: 'unset', padding: 0, margin: 0 }}
            disabled={previewOnly}
          />
        </Box>
      )
    },
  },
]

const resolveFileIcon = (file: IFile) => {
  let url = ''
  if (file instanceof File) {
    url = URL.createObjectURL(file)
  }

  const getMediaElement = (src: string) => (
    <img src={src} style={{ objectFit: 'cover', width: '100%', height: '100%', maxHeight: 300 }} />
  )

  if (file?.extension === uploadInputZones.TEXT_FIELD) {
    return <TextFieldsIcon sx={[iconStyle]} />
  }

  if (file?.extension?.includes('text/') || file?.extension?.includes('aplication/')) {
    return (
      <Box sx={{ width: '10rem', height: '10rem' }}>
        <DescriptionIcon sx={[iconStyle]} />
      </Box>
    )
  }

  const typeToCheck = file?.extension ?? file?.extension
  if (resolveFileType(typeToCheck) === uploadInputZones.AUDIO) {
    return <AudioFileIcon sx={[iconStyle]} />
  }

  if (resolveFileType(typeToCheck) === uploadInputZones.VIDEO) {
    return (
      <Box id='video'>
        <video style={{ objectFit: 'cover', width: '100%', height: '100%', maxHeight: 300 }}>
          <source src={file instanceof File ? url : file?.link}></source>
        </video>
      </Box>
    )
  }

  if (file?.link) {
    return getMediaElement(file instanceof File ? url : file?.link)
  }

  return getMediaElement(file instanceof File ? url : file?.link)
}

const buttonsGroupStyle = {
  '&.MuiToggleButtonGroup-root': {
    width: '100%',
    border: 'none',
  },
}

const buttonBorderRight = {
  borderRight: `1px solid ${colors.TAG_LINES_GRAY}`,
}

const buttonsGroupButton = {
  display: 'flex',
  gap: '.5rem',
  width: '25%',
  border: 'none',
  color: colors.BLACK,
  '&.Mui-selected': {
    backgroundColor: colors.TAG_GRAY_FILL,
    color: colors.BLACK,
  },
  '&:active': {
    backgroundColor: colors.TAG_GRAY_FILL,
    color: colors.BLACK,
  },
  '&.Mui-selected:hover': {
    backgroundColor: colors.TAG_GRAY_FILL,
    color: colors.BLACK,
  },
}

const customChipStyle = {
  backgroundColor: colors.TAG_GRAY_FILL,
  border: `1px solid ${colors.TAG_LINES_GRAY}`,
  borderRadius: '3px',
}

export default {
  testColumns,
  buttonsGroupStyle,
  buttonBorderRight,
  buttonsGroupButton,
  defaultFilter,
  sectionNames,
  customChipStyle,
  resolveFileIcon,
}
